import axios from "axios";

// Get the API URL from environment variables
const API_URL = import.meta.env.VITE_API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  timeout: 30000,
  withCredentials: true,
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  decompress: true,
  httpAgent: false,
  httpsAgent: false,
  maxRedirects: 0, // Don't follow redirects automatically
  validateStatus: (status) => {
    return (status >= 200 && status < 300) || status === 302 || status === 307; // Accept redirect status codes
  },
});

// Add this near the top of the file where api is configured
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Optionally: set a global error state or emit an event
      // Do NOT redirect here
    }
    return Promise.reject(error);
  }
);

export const AuthAPI = {
  validateAuth0Token: async () => {
    const response = await api.post("/v1/auth0/validate");
    return response.data;
  },
};

// Terraform Graph API methods
export const TerraformAPI = {
  generateConfig: async ({
    prompt,
    conversation_id,
    token,
    project_id,
  }: {
    prompt: string;
    conversation_id: string;
    token: string;
    project_id: string;
  }) => {
    // Only send required fields in the body
    const body = JSON.stringify({
      prompt,
      conversation_id,
      project_id,
    });

    const response = await fetch(`/v1/chat/completions?stream=true`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: `Bearer ${token}`,
      },
      body,
    });

    if (!response.ok) {
      throw new Error("Opps we ran into an error. Please try again later.");
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    return {
      stream: async function* () {
        if (!reader) return;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          yield chunk;
        }
      },
    };
  },
};

export const FeatureFlagAPI = {
  // Get all feature flags
  getAllFeatureFlags: async (): Promise<Record<string, boolean>> => {
    const response = await api.get(`/v1/feature-flags/`);
    return response.data;
  },

  // Get a specific feature flag
  getSpecificFeatureFlag: async (
    flagName: string
  ): Promise<{ enabled: boolean }> => {
    const response = await api.get(`/v1/feature-flags/${flagName}`);
    return response.data;
  },

  // Backward compatibility - keeping the old method name
  getFeatureFlag: async (): Promise<Record<string, boolean>> => {
    return FeatureFlagAPI.getAllFeatureFlags();
  },
};

export const ChatFeedbackAPI = {
  // Send feedback (like, dislike, redo) for a specific assistant message
  sendFeedback: async ({
    conversation_id,
    message_id,
    feedback,
  }: {
    conversation_id: string;
    message_id: string;
    feedback: "like" | "dislike" | "redo";
  }) => {
    await api.post("/v1/chat/feedback", {
      conversation_id,
      message_id,
      feedback,
    });
  },
};

// Chat History API methods
export const ChatHistoryAPI = {
  /**
   * Fetch paginated messages for a conversation
   * @param conversationId Conversation UUID
   * @param token Auth bearer token (optional)
   * @param limit Number of messages to fetch (default 100)
   * @param offset Pagination offset (default 0)
   */
  getMessages: async (
    conversationId: string,
    token?: string,
    limit: number = 100,
    offset: number = 0
  ): Promise<{
    total: number;
    limit: number;
    offset: number;
    messages: any[];
  }> => {
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const response = await api.get(
      `/v1/chat/history/${conversationId}/messages`,
      {
        params: { limit, offset },
        headers,
      }
    );
    return response.data;
  },

  /** Delete a conversation (soft-delete) */
  deleteConversation: async (conversationId: string, token?: string) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    await api.delete(`/v1/chat/history/${conversationId}`, { headers });
  },
};

export const ApiKeyAPI = {
  listKeys: async (): Promise<{
    api_keys: {
      id: number;
      name: string;
      masked_key: string;
      created_at: string;
    }[];
  }> => {
    const res = await api.get("/v1/api-keys");
    return res.data;
  },

  createKey: async (
    name: string
  ): Promise<{ id: number; name: string; api_key: string }> => {
    const res = await api.post("/v1/api-key", { name });
    return res.data;
  },

  deleteKey: async (id: number): Promise<void> => {
    await api.delete(`/v1/api-key/${id}`);
  },
};

// ---------------------------------------------------------------------------
// Chat file uploads
// ---------------------------------------------------------------------------

export const ChatFileAPI = {
  /**
   * Upload attachments for a conversation.
   * Returns array of { id, filename, mime_type, size, expires_at, url }
   */
  uploadFiles: async ({
    conversationId,
    files,
    projectId,
    token,
  }: {
    conversationId: string;
    files: File[];
    projectId?: number | null;
    token: string;
  }): Promise<
    {
      id: number;
      filename: string;
      mime_type: string;
      size: number;
      expires_at: string;
      url: string;
    }[]
  > => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("conversation_id", conversationId);
    if (projectId) formData.append("project_id", String(projectId));

    try {
      const res = await api.post<{ files: any }>(
        "/v1/chat/files/upload",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return res.data.files;
    } catch (err: any) {
      /*
       * Surface clearer error messages coming from the FastAPI backend so the
       * UI can show more helpful feedback (e.g. file too large, unsupported
       * mime-type, PII detected, quota exceeded, etc.).
       */
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "File upload failed";
      // Re-throw a plain Error so callers can discriminate via message.
      throw new Error(message);
    }
  },
};

export default api;
